import React from 'react';

import MetricLoadingView from '../../../components/MetricLoadingView.jsx';

import Panel from '../../../components/Panel.jsx';

import {
    useIsExporting,
    useRegisterSlideElementsForPPTExport
} from '../../../hooks/useExport.js';

import {
    useSetExportConfig
} from '../../../hooks/exportConfigurationHooks.js';

import SingleGlanceMetricDisplay from './SingleGlanceMetricDisplay.jsx';

import useListeningCurationFilterValues from '../hooks/useListeningCurationFilterValues.js';

import {
    listeningDefinitions
} from './enums/listeningDefinitions.jsx';

const {
    title,
    description
} = listeningDefinitions['glance-widget'];

export default function GlanceWidget({instanceId, showDeltas, showSparklines, appSpecificExportConfig}) {

    const metricFilters = useListeningCurationFilterValues(instanceId);

    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title,
            subtitle: appSpecificExportConfig?.timePeriodStr
        }
    });

    const interval = 'day';

    const glanceMetrics = ['count', 'impressions', 'authors', 'netSentiment'];
    const nameOverrides = {
        count: 'Posts'
    };

    const metricRequests = _.reduce(glanceMetrics, (memo, glanceMetric) => {
        memo[glanceMetric] = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [],
            measure: glanceMetric,
            filters: metricFilters
        };
        return memo;
    }, {});

    if (showSparklines) {
        _.each(glanceMetrics, metricName => {
            metricRequests[`${metricName}ByDay`] = {
                dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
                dimensions: [interval],
                measure: metricRequests[metricName].measure,
                filters: metricFilters
            };
        });
    }

    if (showDeltas) {
        _.each(glanceMetrics, metricName => {
            const baseMetricRequest = metricRequests[metricName];

            // This calculation of the comparePeriod is a placeholder while we figure out #34153.
            const mainPeriodDuration = moment.utc(baseMetricRequest.filters.timePeriod.endDate).diff(moment.utc(baseMetricRequest.filters.timePeriod.startDate), 'days');

            metricRequests[`${metricName}DeltaRel`] = {
                dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
                dimensions: [],
                measure: {
                    field: baseMetricRequest.measure,
                    transform: 'deltaRelative',
                    transformConfig: {
                        comparePeriodStartDate: moment.utc(baseMetricRequest.filters.timePeriod.startDate).subtract(mainPeriodDuration, 'days').startOf('day').toISOString(),
                        comparePeriodEndDate: baseMetricRequest.filters.timePeriod.startDate
                    }
                },
                filters: metricFilters
            };
        });
    }

    useSetExportConfig(instanceId, {
        title,
        supportedExportFormats: _.values(RivalIQ.enums.exportFormat),
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        csvExportConfig: {
            type: 'dataSourceMetricsGenerator',
            // NOTE we use `glanceMetrics` to avoid including sparkline, or
            // other extra data, metrics in the CSV output
            metricGroups: [_.map(glanceMetrics, (key) => metricRequests[key])]
        },
        appSpecificExportConfig
    });

    const isExporting = useIsExporting();
    const gridClass = isExporting ? 'tlw-grid-cols-4' : 'sm_tlw-grid-cols-4';

    return (
        <Panel
            title={title}
            description={description}
            exportSubtitle={appSpecificExportConfig?.timePeriodStr}
            instanceId={instanceId}
            supportedExportFormats={_.values(RivalIQ.enums.exportFormat)}
            supportedExportTypes={_.values(RivalIQ.enums.exportType)}
        >

            <MetricLoadingView
                instanceId={instanceId}
                metricRequests={metricRequests}>
                {
                    (metricResults) => (
                        <div className={`tlw-grid tlw-grid-cols-1 tlw-gap-5 tlw-p-7.5 ${gridClass}`}>
                            {
                                _.map(glanceMetrics, metricName => {
                                    return <SingleGlanceMetricDisplay
                                        key={metricName}
                                        metricRequest={metricRequests[metricName]}
                                        metricResult={metricResults[metricName]}
                                        metricName={nameOverrides[metricName]}
                                        renderSparkLine={showSparklines}
                                        metricResultForSparkLine={metricResults[`${metricName}ByDay`]}
                                        deltaRelativeRequest={metricRequests[`${metricName}DeltaRel`]}
                                        deltaRelativeResult={metricResults[`${metricName}DeltaRel`]}
                                    />;
                                })
                            }
                        </div>
                    )
                }
            </MetricLoadingView>
        </Panel>
    );
}
